import {
  CTabContent,
  CSpinner,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody,
} from "@coreui/react";
import BoxWrapper from "../../components/BoxWrapper";
import { usePayoutsQuery } from "../../hooks/payouts/usePayoutsQuery";
import { general } from "../../locales/general";
import { useAuth } from "../../hooks/useAuth";
import { useState } from "react";
import WidthrawalRequestModal from "../../components/modals/WithdrawalRequestModal";
import Pagination from "../../components/Pagination";
import { useSelectedPlace } from "../../hooks/place/useSelectedPlace";
import { usePlacePendingAmount } from "../../hooks/payment/usePlacePendingAmount";
import {
  usePlacePendingAmountBreakdown,
  PlacePendingAmountBreakdownItem,
} from "../../hooks/payment/usePlacePendingAmountBreakdown";
import { formatCurrency } from "../../helpers/general";
import { useSearchParams } from "react-router-dom";

const MoneySvg = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6" />
      <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
      <path d="M7 6h1v4" />
      <path d="m16.71 13.88.7.71-2.82 2.82" />
    </svg>
  );
};

const BreakdownGroup = ({
  itemKey,
  label,
  items,
}: {
  itemKey: string;
  label: string;
  items: PlacePendingAmountBreakdownItem[];
}) => {
  const total = items.reduce((sum, item) => sum + item.amount, 0);

  return (
    <CAccordionItem itemKey={itemKey}>
      <CAccordionHeader>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <span>{label}</span>
          <span className="fw-semibold">{formatCurrency(total)} DH</span>
        </div>
      </CAccordionHeader>
      <CAccordionBody>
        {items.length > 0 ? (
          <ul className="list-unstyled mb-0">
            {items.map((item) => (
              <li
                key={item.id}
                className="d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <span>{item.name}</span>
                <span className="d-flex align-items-center gap-2">
                  <span className="text-muted fs-7">
                    {item.bookingsCount}{" "}
                    {item.bookingsCount > 1 ? "réservations" : "réservation"}
                  </span>
                  <span className="fw-semibold">
                    {formatCurrency(item.amount)} DH
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-center text-muted mb-0">{general.fr.noData}</p>
        )}
      </CAccordionBody>
    </CAccordionItem>
  );
};

const PAGE_SIZE = 10;
export default function Payouts() {
  const { isAdmin, user } = useAuth();
  const [withdrawalModalVisible, setWithdrawalModalVisible] = useState(false);
  const { totalPages, currentPage, setCurrentPage } = usePayoutsQuery();
  const [searchParams] = useSearchParams();

  const { selectedPlace, selectedPlaceId } = useSelectedPlace(user, isAdmin);
  const placeId =
    searchParams.get("place") ||
    selectedPlaceId ||
    selectedPlace?.id ||
    user?.adminForPlace?.id;
  const { pendingAmount, isLoading: isPendingAmountLoading } =
    usePlacePendingAmount(placeId);
  const {
    events,
    offers,
    packs,
    isLoading: isBreakdownLoading,
  } = usePlacePendingAmountBreakdown(placeId);

  return (
    <section>
      <h5 className="fs-4 fw-bold mb-2">Paiements</h5>
      <BoxWrapper>
        <CTabContent>
          <div
            className="d-flex justify-content-between align-items-baseline before-table"
            id="payouts"
          >
            {/* {!isAdmin ? (
              <button
                className="outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center shadow-secondary mx-5 my-3"
                onClick={() => setWithdrawalModalVisible(true)}
                disabled={payouts?.length === 0}
              >
                Retirer le solde
              </button>
            ) : (
              <div />
            )} */}
            <div></div>
            {placeId && (
              <div
                className="d-flex flex-column align-items-end gap-1 bg-gradient-primary p-3 text-white mx-3 my-3"
                style={{
                  borderTopRightRadius: "8px",
                  alignSelf: "flex-start",
                  minWidth: "140px",
                }}
              >
                <span className="fs-7 opacity-75">Solde</span>
                <div className="d-flex align-items-center gap-2">
                  <MoneySvg />
                  {isPendingAmountLoading ? (
                    <CSpinner size="sm" color="light" />
                  ) : (
                    <p className="fs-5 fw-bold m-0">
                      {formatCurrency(pendingAmount)} DH
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          {placeId && (
            <div className="mx-3 mb-3">
              <h6 className="fw-semibold mb-2">Répartition du solde</h6>
              {isBreakdownLoading ? (
                <div className="text-center py-3">
                  <CSpinner size="sm" />
                </div>
              ) : (
                <CAccordion flush>
                  <BreakdownGroup
                    itemKey="events"
                    label="Événements"
                    items={events}
                  />
                  <BreakdownGroup
                    itemKey="offers"
                    label="Offres spéciales"
                    items={offers}
                  />
                  <BreakdownGroup itemKey="packs" label="Packs" items={packs} />
                </CAccordion>
              )}
            </div>
          )}

          <WidthrawalRequestModal
            alert="Veuillez saisir le montant à retirer"
            open={withdrawalModalVisible}
            onAccept={() => {
              setWithdrawalModalVisible(false);
            }}
            onClose={() => setWithdrawalModalVisible(false)}
          />
        </CTabContent>
      </BoxWrapper>
      {/* TODO: Add pagination */}
      <Pagination
        currentPage={currentPage}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
        siblingCount={0}
        totalCount={totalPages}
        pageSize={PAGE_SIZE}
      />
    </section>
  );
}
